import styles from '@components/components/PhotovoltaicsCalculator/PostalCodeQueryWidget/PostalCodeQueryWidget.module.scss';
import AutocompleteInputField from '@components/components/AddressPicker/components/AutocompleteInputField/AutocompleteInputField';
import { useAjax, useLocalStorage } from '@components/hooks';
import api from '@components/api';
import {
  Button,
  Card,
  Content,
  FontAwesomeProIcon,
  Heading,
  Link,
} from '@wienenergiegithub/ui-next';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormStore, useMenuStore, Form, FormSubmit } from '@ariakit/react';
import debounce from 'lodash.debounce';
import { CancelToken } from 'axios';

let cancel;

const Available = ({ onClick, postalCode, availableSite }) => (
  <div className={styles.availableContainer}>
    <div className={styles.checkmark}>
      <FontAwesomeProIcon icon="check" />
    </div>
    <Heading as="h3" className={styles.availableHeading} size="h5">
      Für Ihr Wohngebiet bieten wir PV-Anlagen an
    </Heading>
    <Content className={styles.availablePostalCode}>{postalCode}</Content>
    <Button
      className={styles.checkFurtherButton}
      onClick={onClick}
      theme="tertiary"
      variant="link"
    >
      WEITERE ADRESSE PRÜFEN
    </Button>
    <Button
      render={
        <Link target={availableSite?.target} to={availableSite?.url}>
          Zum PV-Rechner
        </Link>
      }
    />
  </div>
);

const PostalCodeQueryWidget = ({ availableSite, unavailableSite }) => {
  const [storage, setStorage] = useLocalStorage('pv-available', {
    plz: '',
    isAvailable: false,
  });
  const [submitPostalCode] = useAjax('we_theme_pv_availability');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedPostalCode, setSelectedPostalCode] = useState(storage.plz);
  const [allowSubmit, setAllowSubmit] = useState(false);
  const menu = useMenuStore({
    animated: 250,
    orientation: 'vertical',
    placement: 'bottom-start',
  });
  const [values, setValues] = useState({
    plz: storage?.plz || '',
  });
  const form = useFormStore({ values, setValues });

  form.useValidate(() => {
    if (!values.plz) {
      form.setError('plz', 'Bitte geben Sie eine PLZ ein');
    }
  });

  form.useSubmit(async () => {
    if (allowSubmit) {
      try {
        const { data: response } = await submitPostalCode({
          plz: selectedPostalCode.split(' ')[0],
        });

        if (response.success) {
          if (response.data.isAvailable) {
            setStorage({ plz: selectedPostalCode, isAvailable: true });
            return;
          }

          window.open(unavailableSite.url, '_self');
          setStorage({
            plz: '',
            isAvailable: false,
          });
        }

        throw new Error(response.data[0].message);
      } catch (err) {
        /* empty */
      }
    }
  });

  const cancelOngoingRequest = () => {
    if (cancel !== undefined) {
      cancel();
    }
  };

  const queryAddress = async (_, value) => {
    cancelOngoingRequest();

    const postValues = {
      field: 'plzOrt',
      search: value,
    };
    try {
      let { data: suggestionsList } = await api.post(
        '/address/query',
        postValues,
        {
          cancelToken: new CancelToken(c => {
            cancel = c;
          }),
        },
      );
      suggestionsList = suggestionsList.map(suggestion => ({
        label: suggestion.plzOrt.join(' '),
        value: suggestion.plzOrt.join(' '),
        icon: true,
        type: 'result',
      }));

      if (suggestionsList.length === 1) {
        setSelectedPostalCode(suggestionsList[0].label);
        setAllowSubmit(true);
        return;
      }

      setSuggestions(suggestionsList);
    } catch (error) {
      if (error?.response?.status === 404) {
        setSuggestions([
          {
            label: `${value} ist keine gültige PLZ`,
            value: '',
            icon: false,
            type: 'error',
          },
        ]);
      }
    } finally {
      menu.show();
    }
  };

  const handleSuggestionClick = (_, value) => {
    setSelectedPostalCode(value);
    setAllowSubmit(true);
  };
  const handleResetValue = () => {
    setSuggestions([]);
    setSelectedPostalCode('');
    setStorage({ plz: '', isAvailable: false });
    form.setValue('plz', '');
    setAllowSubmit(false);
  };

  const debouncedQueryAddress = useCallback(
    debounce((currentField, value) => queryAddress(currentField, value), 500),
    [],
  );

  useEffect(() => {
    if (!values.plz) {
      return;
    }
    debouncedQueryAddress('plz', values.plz);
  }, [values.plz]);

  return (
    <Card className={styles.widgetContainer}>
      <div className={styles.header}>
        <FontAwesomeProIcon className={styles.icon} icon="home" />
        <Heading as="h3" className={styles.heading} noSpacing size="h6">
          Standort
        </Heading>
      </div>
      {!storage?.isAvailable && (
        <div className={styles.addressCheckerContainer}>
          <Content className={styles.description}>
            Bitte geben Sie hier die Postleitzahl Ihrer potentiellen PV-Anlage
            an. So sehen Sie sofort, ob wir in diesem Gebiet PV-Anlagen
            anbieten.
          </Content>
          <Form store={form}>
            <div className={styles.form}>
              <div className={styles.formInput}>
                <AutocompleteInputField
                  handleResetValue={handleResetValue}
                  handleSuggestionClick={handleSuggestionClick}
                  label="PLZ:"
                  menu={menu}
                  name="plz"
                  store={form}
                  suggestions={suggestions}
                  value={selectedPostalCode}
                />
              </div>
              <FormSubmit
                render={
                  <Button
                    className={styles.submitButton}
                    disabled={!allowSubmit}
                  />
                }
                store={form}
              >
                Überprüfen
              </FormSubmit>
            </div>
          </Form>
        </div>
      )}
      {storage?.isAvailable && (
        <Available
          availableSite={availableSite}
          onClick={handleResetValue}
          postalCode={storage.plz}
        />
      )}
    </Card>
  );
};

export default PostalCodeQueryWidget;
